import React, { useState, useRef, useEffect } from 'react';
import { Utils, authJsonHeaders } from '../../utils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faShieldAlt, faTriangleExclamation, faKey, faMobileAlt, faComments } from '@fortawesome/free-solid-svg-icons';
import { FiUser } from "react-icons/fi";
import logo from "../../assets/notifysphere-logo.png";
import './TwoFactorVerify.css';

const SUPPORT_MAILTO =
  (typeof process !== "undefined" && process.env.REACT_APP_SUPPORT_EMAIL)
    ? `mailto:${process.env.REACT_APP_SUPPORT_EMAIL}`
    : "mailto:info@notifysphere.in";

function TwoFactorVerify({ userId, email, onSuccess, onCancel, pending2FAToken = null }) {
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [recoveryInput, setRecoveryInput] = useState('');
  const [useRecoveryMode, setUseRecoveryMode] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const inputRefs = useRef([]);

  useEffect(() => {
    if (!useRecoveryMode && inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, [useRecoveryMode]);

  const handleChange = (index, value) => {
    if (value && !/^\d$/.test(value)) return;

    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);
    setError('');

    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    if (value && index === 5) {
      const fullCode = newCode.join('');
      if (fullCode.length === 6) {
        handleVerify(fullCode);
      }
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pastedData) {
      const newCode = [...code];
      for (let i = 0; i < pastedData.length; i++) {
        newCode[i] = pastedData[i];
      }
      setCode(newCode);
      const focusIndex = Math.min(pastedData.length, 5);
      inputRefs.current[focusIndex]?.focus();

      if (pastedData.length === 6) {
        handleVerify(pastedData);
      }
    }
  };

  const handleVerify = async (verificationCode) => {
    let codeToVerify;
    if (useRecoveryMode) {
      codeToVerify = (recoveryInput || "").replace(/\s+/g, "").toLowerCase();
      if (codeToVerify.length < 8) {
        setError('Enter your full recovery code (8 characters).');
        return;
      }
    } else {
      codeToVerify = verificationCode || code.join('');
      if (codeToVerify.length !== 6) {
        setError('Please enter all 6 digits');
        return;
      }
    }

    setIsLoading(true);
    setError('');

    try {
      const response = await fetch(Utils.baseurl + '2fa/validate', {
        method: 'POST',
        headers: authJsonHeaders(Utils.storageKeys.token, pending2FAToken),
        body: JSON.stringify({
          userId: userId,
          token: codeToVerify
        }),
      });

      const result = await response.json();

      if (result.status) {
        onSuccess(result);
      } else {
        setError(result.message || 'Invalid code. Please try again.');
        if (!useRecoveryMode) {
          setCode(['', '', '', '', '', '']);
          inputRefs.current[0]?.focus();
        } else {
          setRecoveryInput('');
        }
      }
    } catch (err) {
      console.error('2FA verification error:', err);
      setError('Network error. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    handleVerify();
  };

  const switchToAuthenticator = () => {
    setUseRecoveryMode(false);
    setRecoveryInput('');
    setError('');
  };

  const switchToRecovery = () => {
    setUseRecoveryMode(true);
    setCode(['', '', '', '', '', '']);
    setError('');
  };

  const canSubmit = useRecoveryMode
    ? (recoveryInput || "").replace(/\s+/g, "").length >= 8
    : code.join('').length === 6;

  return (
    <div className="twofa-verify-page">
      <div className="page_logo">
        <a href="/" style={{ textAlign: "center" }}>
          <img src={logo} alt="notifySphere" />
        </a>
      </div>

      <div className="twofa-verify-container">
        <div className="twofa-icon-wrapper">
          <div className="twofa-icon">
            <FontAwesomeIcon icon={faShieldAlt} />
          </div>
          <div className="twofa-icon-pulse"></div>
        </div>

        <h1>Two-Factor Authentication</h1>
        <p className="twofa-subtitle">
          {useRecoveryMode
            ? 'Enter one recovery code you saved when you enabled 2FA (8 characters).'
            : 'Enter the 6-digit code from your authenticator app'}
        </p>

        {email && (
          <div className="twofa-email-badge">
            <span className="email-icon"><FiUser size={18} /></span>
            <span className="email-text">{email}</span>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {!useRecoveryMode ? (
            <div className="code-input-container">
              <div className="code-inputs">
                {code.map((digit, index) => (
                  <input
                    key={index}
                    ref={(el) => (inputRefs.current[index] = el)}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handleChange(index, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(index, e)}
                    onPaste={handlePaste}
                    className={`code-input ${error ? 'error' : ''} ${digit ? 'filled' : ''}`}
                    disabled={isLoading}
                    autoComplete="one-time-code"
                    aria-label={`Digit ${index + 1}`}
                  />
                ))}
              </div>
              <div className="code-separator">
                <span></span>
                <span></span>
                <span></span>
              </div>
            </div>
          ) : (
            <div className="twofa-recovery-input-wrap">
              <input
                type="text"
                className={`twofa-recovery-input ${error ? 'error' : ''}`}
                placeholder="e.g. a1b2c3d4"
                value={recoveryInput}
                onChange={(e) => {
                  setRecoveryInput(e.target.value);
                  setError('');
                }}
                disabled={isLoading}
                autoComplete="off"
                spellCheck={false}
              />
            </div>
          )}

          {error && (
            <div className="twofa-error">
              <span className="error-icon"><FontAwesomeIcon icon={faTriangleExclamation} /></span>
              <span>{error}</span>
            </div>
          )}

          <button
            type="submit"
            className={`twofa-verify-btn ${isLoading ? 'loading' : ''}`}
            disabled={isLoading || !canSubmit}
          >
            {isLoading ? (
              <>
                <FontAwesomeIcon icon={faSpinner} spin />
                <span>Verifying...</span>
              </>
            ) : (
              <>
                <span>Verify & Continue</span>
                <span className="btn-arrow">→</span>
              </>
            )}
          </button>
        </form>

        <div className="twofa-divider">
          <span>Need help?</span>
        </div>

        <div className="twofa-help">
          {!useRecoveryMode ? (
            <button type="button" className="help-link" onClick={switchToRecovery}>
              <span className="help-icon"><FontAwesomeIcon icon={faKey} /></span>
              Use recovery code
            </button>
          ) : (
            <button type="button" className="help-link" onClick={switchToAuthenticator}>
              <span className="help-icon"><FontAwesomeIcon icon={faMobileAlt} /></span>
              Use authenticator app
            </button>
          )}
          <a className="help-link help-link-anchor" href={SUPPORT_MAILTO}>
            <span className="help-icon"><FontAwesomeIcon icon={faComments} /></span>
            Contact support
          </a>
        </div>

        <button className="twofa-back-btn" onClick={onCancel}>
          <span className="back-arrow">←</span>
          Back to Sign In
        </button>
      </div>
    </div>
  );
}

export default TwoFactorVerify;
